import React from 'react';
import { FeedbackStatusProps } from '@/types/components';
import { StatusVariant } from '@/types/common';
import { StatusBadge, type BadgeStatus } from '@/design-system/badge';
import { cn } from '@/design-system/lib/utils';

// Predefined status mappings for common statuses
const defaultStatusMappings: Record<string, StatusVariant> = {
  // Success states
  complete: 'success',
  completed: 'success',
  active: 'success',
  approved: 'success',
  delivered: 'success',
  online: 'success',

  // Warning states
  'in delivery': 'warning',
  'in progress': 'warning',
  'pending pickup': 'warning',
  'picking up': 'warning',
  processing: 'warning',

  // Error states
  failed: 'error',
  error: 'error',
  rejected: 'error',
  offline: 'error',
  unavailable: 'error',

  // Info states
  allocating: 'info',
  queueing: 'info',
  scheduled: 'info',
  reviewing: 'info',

  // Neutral states
  canceled: 'neutral',
  cancelled: 'neutral',
  draft: 'neutral',
  idle: 'neutral',

  // Pending states
  pending: 'pending',
  waiting: 'pending',
  hold: 'pending',
};

/** App StatusVariant → kit status. */
const VARIANT_TO_STATUS: Record<StatusVariant, BadgeStatus> = {
  success: 'success',
  warning: 'away',
  error: 'error',
  info: 'information',
  neutral: 'neutral',
  pending: 'warning',
};

export const FeedbackStatus: React.FC<FeedbackStatusProps> = ({
  status,
  variant,
  size = 'medium',
  customColors,
  className = '',
}) => {
  // Determine variant automatically if not provided
  const resolvedVariant =
    variant || defaultStatusMappings[status.toLowerCase()] || 'neutral';

  // Custom colors bypass the kit and keep the legacy plain-span rendering
  // with inline styles.
  if (customColors) {
    const sizeClasses = {
      small: 'px-2 py-0.5 text-[11px]',
      medium: 'px-2.5 py-0.5 text-[11px]',
      large: 'px-3 py-1 text-xs',
    };

    return (
      <span
        className={cn(
          'inline-flex items-center rounded-sm font-medium tracking-tight',
          sizeClasses[size],
          className
        )}
        style={{
          backgroundColor: customColors.backgroundColor,
          color: customColors.textColor,
        }}
      >
        {status}
      </span>
    );
  }

  return (
    <StatusBadge
      variant="dot-light"
      status={VARIANT_TO_STATUS[resolvedVariant]}
      size={size === 'large' ? 'md' : 'sm'}
      className={cn(className)}
    >
      {status}
    </StatusBadge>
  );
};

export default FeedbackStatus;
